/**
 * Regulatory commitment register (P429.2).
 *
 * The named returns, notifications, limits and licence conditions a
 * bank must meet -- what the control-derived obligation catalogue does
 * not hold by name. An admitted service is bound by or produces these
 * (the edge lives on the service drawer), and the layer view reads
 * them as the regulatory layer.
 */
import type { AdminColumn, EntityAdapter, FormField } from "./types";

export type CommitmentKind = "return" | "notification" | "limit" | "licence_condition";

const KIND_LABELS: Record<CommitmentKind, string> = {
  return: "Regulatory return",
  notification: "Notification",
  limit: "Limit",
  licence_condition: "Licence condition",
};

export interface RegulatoryCommitment {
  id: string;
  code: string;
  name: string;
  kind: CommitmentKind;
  cadence: string | null;
  consequence: string | null;
  obligation_id: string | null;
  description: string | null;
  created_at: string;
  updated_at: string;
}

export interface RegulatoryCommitmentIn {
  code: string;
  name: string;
  kind: CommitmentKind;
  cadence: string | null;
  consequence: string | null;
  description: string | null;
}

export type RegulatoryCommitmentPatch = Partial<Omit<RegulatoryCommitmentIn, "code">>;

const columns: AdminColumn<RegulatoryCommitment>[] = [
  {
    key: "name",
    label: "Commitment",
    sortValue: (r) => r.name,
    render: (r) => (
      <span>
        <span className="font-medium text-slate-900">{r.name}</span>
        <span className="ms-2 font-mono text-xs text-slate-500">
          <span aria-hidden="true"> · </span>
          {r.code}
        </span>
      </span>
    ),
  },
  {
    key: "kind",
    label: "Kind",
    sortValue: (r) => r.kind,
    render: (r) => (
      <span className="text-xs text-slate-700">{KIND_LABELS[r.kind]}</span>
    ),
  },
  {
    key: "cadence",
    label: "When due",
    render: (r) =>
      r.cadence ? (
        <span className="text-xs text-slate-700">{r.cadence}</span>
      ) : (
        <span className="text-xs text-slate-400">--</span>
      ),
  },
];

const formFields: FormField<RegulatoryCommitmentIn>[] = [
  { kind: "text", key: "code", label: "Code", required: true, maxLength: 64 },
  { kind: "text", key: "name", label: "Name", required: true, maxLength: 255 },
  {
    kind: "select",
    key: "kind",
    label: "Kind",
    required: true,
    options: (Object.keys(KIND_LABELS) as CommitmentKind[]).map((v) => ({
      value: v,
      label: KIND_LABELS[v],
    })),
  },
  { kind: "text", key: "cadence", label: "When due" },
  { kind: "textarea", key: "consequence", label: "What missing it costs", rows: 2 },
  { kind: "textarea", key: "description", label: "Description", rows: 3 },
];

export const regulatoryCommitmentAdapter: EntityAdapter<
  RegulatoryCommitment,
  RegulatoryCommitmentIn,
  RegulatoryCommitmentPatch
> = {
  i18nPrefix: "modules.foundation.admin.regulatoryCommitments",
  importKind: "regulatory_commitment",
  endpoint: "/v1/admin/foundation/regulatory-commitments",
  queryKey: ["foundation-admin", "regulatory-commitments"] as const,
  columns,
  defaultSort: { key: "name", dir: "asc" },
  rowKey: (r) => r.id,
  rowSearchHaystack: (r) => `${r.code} ${r.name} ${r.kind} ${r.cadence ?? ""}`,
  formFields,
  formInitial: () => ({
    code: "",
    name: "",
    kind: "return",
    cadence: null,
    consequence: null,
    description: null,
  }),
  rowToPatch: (r) => ({
    name: r.name,
    kind: r.kind,
    cadence: r.cadence,
    consequence: r.consequence,
    description: r.description,
  }),
};
